<script setup lang="ts">
// The player agreements: /legal/terms, /legal/privacy, /legal/contest-rules.
// One page for all three — they differ only in content, and a shared shell
// keeps the reading experience (and the interim notice) identical.
//
// Public by design: `auth-optional` so the documents are reachable before an
// account exists, which is the whole point — you agree to them AT registration.
import { LEGAL_DOCS, LEGAL_DOC_SLUGS, type LegalDocSlug } from '~/utils/legalDocs'

definePageMeta({ middleware: 'auth-optional' })

const route = useRoute()
const slug = computed(() => String(route.params.doc || ''))
const doc = computed(() => LEGAL_DOCS[slug.value as LegalDocSlug])

// unknown slug -> the themed error boundary (error.vue), not a blank page
if (!(LEGAL_DOC_SLUGS as readonly string[]).includes(slug.value)) {
  throw createError({ statusCode: 404, statusMessage: 'Document not found', fatal: true })
}
</script>

<template>
  <div>
    <!-- plain title, no crumbs: the three documents are peers with no parent
         page, and the tab strip below is the navigation between them -->
    <AppNav :title="doc?.title || 'Legal'" />
    <div class="wrap">
      <!-- honest about what these are until counsel clears them; see
           frontend/utils/legalDocs.ts and main.py LEGAL_DOCS_VERSION -->
      <p class="interim">
        Interim text, pending final legal review. Some details are marked as placeholders.
      </p>
      <nav class="doc-tabs">
        <NuxtLink v-for="s in LEGAL_DOC_SLUGS" :key="s" :to="`/legal/${s}`"
          class="doc-tab" :class="{ on: s === slug }">{{ LEGAL_DOCS[s].title }}</NuxtLink>
      </nav>
      <!-- build-time content from our own repo, never user input -->
      <article class="doc" v-html="doc?.html" />
    </div>
  </div>
</template>

<style scoped>
.wrap { max-width: 760px; margin: 0 auto; padding: 20px 20px 64px; }
.interim {
  font-size: 12px; color: var(--muted); border: 1px solid var(--line);
  border-radius: 10px; padding: 10px 12px; margin: 0 0 18px;
}
.doc-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 24px; }
.doc-tab {
  font-size: 12px; color: var(--muted); border: 1px solid var(--line);
  background: var(--panel-2); padding: 6px 12px; border-radius: 999px;
}
.doc-tab:hover { color: var(--ink); border-color: var(--brand); }
.doc-tab.on { color: var(--ink); border-color: var(--brand); background: var(--panel); }

.doc { color: var(--ink); font-size: 15px; line-height: 1.65; }
.doc :deep(h1) { font-size: 24px; margin: 0 0 20px; line-height: 1.25; }
.doc :deep(h2) { font-size: 18px; margin: 32px 0 10px; }
.doc :deep(h3) { font-size: 15px; margin: 24px 0 8px; }
.doc :deep(h4) { font-size: 14px; margin: 20px 0 8px; color: var(--muted); }
.doc :deep(p) { margin: 0 0 14px; }
.doc :deep(ul), .doc :deep(ol) { margin: 0 0 14px; padding-left: 22px; }
.doc :deep(li) { margin-bottom: 8px; }
.doc :deep(strong) { color: var(--ink); }
.doc :deep(code) {
  font-family: var(--font-mono); font-size: 13px;
  background: var(--panel-2); padding: 1px 5px; border-radius: 5px;
}
.doc :deep(a) { color: var(--brand); text-decoration: underline; }
</style>
